import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { carouselIcons } from "./CarouselIcons";

const batchSchedule = [
  {
    name: "O Level (Cambridge)",
    days: "Sat, Mon, Wed",
    time: "7:00pm - 8:30pm",
    mode: "Online/Offline",
  },
  {
    name: "A Level (Cambridge)",
    days: "Sun, Tue, Thu",
    time: "8:30pm - 10:00pm",
    mode: "Online/Offline",
  },
  {
    name: "O Level (Edexcel)",
    days: "Sat, Tue, Thu",
    time: "5:30pm - 7:00pm",
    mode: "Online/Offline",
  },
  {
    name: "A Level (Edexcel)",
    days: "Sun, Mon, Wed",
    time: "4:00pm - 5:30pm",
    mode: "Online/Offline",
  },
];

const resources = [
  {
    title: "Lecture Notes & Slides",
    desc: "Comprehensive notes and slides for each topic, updated regularly.",
  },
  {
    title: "Past Papers & Mark Schemes",
    desc: "Access to a wide range of Cambridge and Edexcel past papers with solutions.",
  },
  {
    title: "Practice Assignments",
    desc: "Weekly assignments and programming tasks to reinforce concepts.",
  },
  {
    title: "Doubt Clearing Sessions",
    desc: "Regular sessions to clarify concepts and solve tricky problems.",
  },
  {
    title: "Online Discussion Forum",
    desc: "24/7 access to a student forum for peer and instructor support.",
  },
];

function Learn() {
  // Placeholder data for testimonials and success stories
  const testimonials = [
    {
      name: "Ayesha Rahman",
      quote:
        "The classes are interactive and the resources are super helpful! I feel confident for my O Levels.",
      img: "https://randomuser.me/api/portraits/women/44.jpg",
    },
    {
      name: "Tanvir Ahmed",
      quote:
        "Amazing teaching style and always available for doubts. Highly recommended!",
      img: "https://randomuser.me/api/portraits/men/46.jpg",
    },
    {
      name: "Priya Saha",
      quote:
        "Batch schedules are flexible and the practice sets made all the difference for my A Levels.",
      img: "https://randomuser.me/api/portraits/women/65.jpg",
    },
  ];
  const successStats = [
    { label: "A* / A", value: 120 },
    { label: "B", value: 45 },
    { label: "C", value: 38 },
    { label: "D", value: 15 },
    { label: "Other", value: 12 },
  ];
  const highlights = [
    "100% syllabus completion",
    "Personalized feedback",
    "Weekly assessments",
    "Live problem-solving sessions",
  ];
  const learningResources = [
    {
      title: "Study Materials",
      desc: "All topics covered with notes & slides.",
      color: "bg-blue-50 border-blue-400 text-blue-700",
      icon: "📘",
    },
    {
      title: "Exam Solutions",
      desc: "Detailed solutions for past papers.",
      color: "bg-red-50 border-red-400 text-red-700",
      icon: "📝",
    },
    {
      title: "Practice Tools",
      desc: "Online quizzes & code playground.",
      color: "bg-green-50 border-green-400 text-green-700",
      icon: "🧑‍💻",
    },
    {
      title: "Assignments",
      desc: "Weekly assignments for mastery.",
      color: "bg-purple-50 border-purple-400 text-purple-700",
      icon: "📚",
    },
    {
      title: "Video Lectures",
      desc: "Recorded sessions for revision.",
      color: "bg-yellow-50 border-yellow-400 text-yellow-700",
      icon: "🎥",
    },
    {
      title: "Discussion Forum",
      desc: "24/7 peer & instructor support.",
      color: "bg-indigo-50 border-indigo-400 text-indigo-700",
      icon: "💬",
    },
  ];
  // Vertical carousel taglines
  const carouselItems = [
    {
      text: "Interactive Live Classes",
      iconIdx: 0,
    },
    {
      text: "Personalized Feedback",
      iconIdx: 1,
    },
    {
      text: "Weekly Assessments",
      iconIdx: 2,
    },
    {
      text: "100% Syllabus Coverage",
      iconIdx: 3,
    },
    {
      text: "Exam-Focused Strategies",
      iconIdx: 4,
    },
    {
      text: "Cambridge & Edexcel Expert",
      iconIdx: 5,
    },
  ];
  const [carouselIdx, setCarouselIdx] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCarouselIdx((idx) => (idx + 1) % carouselItems.length);
    }, 2200);
    return () => clearInterval(interval);
  }, [carouselItems.length]);

  return (
    <div className="bg-gradient-to-br from-blue-50 to-blue-100 min-h-screen w-full">
      {/* HERO SECTION */}
      <section className="w-full py-8 bg-gradient-to-r from-blue-100 via-white to-blue-200">
        <div className="container mx-auto px-4 flex flex-col md:flex-row items-center justify-between">
          {/* Left: Text */}
          <div className="flex-1 max-w-xl text-left space-y-4 md:pl-10">
            <h1 className="text-2xl sm:text-lg md:text-2xl font-bold text-blue-900 leading-tight">
              Master Computer Science with
              <br />
              <span style={{ color: "rgb(232, 51, 16)" }}>Fahim Ferdous</span>
            </h1>

            <p className="text-[14px] text-gray-700">
              Online & offline teaching for O/A Level (Class IX–XII), Cambridge
              & Edexcel curriculum. Personalized, modern, and result-driven
              learning.
            </p>
            <div className="flex gap-4 mt-5 text-[14px]">
              <Link to="/signup">
                <button className="flex items-center gap-2 font-semibold bg-[#211033] text-white border border-teal-700 rounded-xl py-2 px-5 max-h-[40px] hover:bg-white hover:text-[#211033] ease-in duration-300 shadow">
                  Join Now
                </button>
              </Link>
              <Link to="/contact">
                <button className="flex items-center gap-2 font-semibold bg-white text-[#211033] border border-[#211033] rounded-xl py-2 px-5 max-h-[40px] hover:bg-[#211033] hover:text-white ease-in duration-300 shadow">
                  View Batches
                </button>
              </Link>
            </div>
          </div>
          {/* Right: Vertical Carousel as Cards */}
          <div className="flex-1 flex justify-center items-center max-w-md w-full mt-8 md:mt-0">
            <div
              className="h-44 w-full max-w-xs flex items-center justify-center relative"
              aria-label="Highlights"
            >
              <AnimatePresence mode="wait">
                {carouselItems.map((item, idx) => {
                  const Icon = carouselIcons[item.iconIdx];
                  return idx === carouselIdx ? (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -30 }}
                      transition={{ duration: 0.6, type: "spring" }}
                      className="absolute left-0 right-0 mx-auto bg-white border-l-4 border-blue-500 rounded-xl shadow-2xl px-6 py-8 flex flex-col items-center justify-center text-blue-800 text-xl font-bold min-h-[7rem]"
                      style={{ minHeight: "7rem" }}
                    >
                      <span className="mb-3 text-4xl text-blue-400">
                        <Icon />
                      </span>
                      <span className="text-center leading-snug">
                        {item.text}
                      </span>
                    </motion.div>
                  ) : null;
                })}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT INSTRUCTOR SECTION */}
      <section className="w-full py-8 bg-white">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center gap-8 px-4">
          {/* About Text */}
          <div className="flex-1 space-y-2 text-center md:text-left">
            <h2 className="text-xl sm:text-2xl font-bold text-blue-800">
              About Fahim Ferdous
            </h2>
            <p className="text-gray-600">
              Dedicated and award-winning educator for O/A Level Computer
              Science
            </p>
            <div className="flex flex-wrap gap-3 justify-center md:justify-start py-2">
              <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-semibold">
                10+ years teaching
              </span>
              <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-semibold">
                600+ students
              </span>
              <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-xs font-semibold">
                100% syllabus coverage
              </span>
              <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-xs font-semibold">
                70% A*/A
              </span>
            </div>
            <ul className="text-gray-700 text-sm mt-2 space-y-1 list-disc list-inside">
              <li>O/A Level specialist (Cambridge & Edexcel)</li>
              <li>Interactive, concept-driven approach</li>
              <li>Result multiplying, exam-focused strategies</li>
              <li>Live mentoring and personal feedback</li>
            </ul>
          </div>
          {/* Instructor Image */}
          <div className="flex-1 flex justify-center">
            <img
              src="/images/profilePic2.jpg"
              alt="Fahim Ferdous"
              className="w-40 h-40 rounded-full border-4 border-blue-200 object-cover shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* BATCHES & SCHEDULE SECTION */}
      <section className="w-full py-8">
        <div className="max-w-5xl mx-auto px-4">
          <h3 className="text-lg sm:text-xl font-bold text-blue-900 mb-4 text-center">
            Batches & Schedule
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {batchSchedule.map((batch, idx) => (
              <div
                key={idx}
                className={`rounded-xl shadow-lg p-5 border-t-4 ${
                  idx % 2 === 0 ? "border-blue-400" : "border-green-400"
                } bg-white flex flex-col gap-2`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <svg width="26" height="26" viewBox="0 0 32 32" fill="none">
                    <rect
                      x="4"
                      y="8"
                      width="24"
                      height="18"
                      rx="4"
                      fill="#DBEAFE"
                    />
                    <rect
                      x="7"
                      y="13"
                      width="18"
                      height="9"
                      rx="2"
                      fill="#3B82F6"
                    />
                    <rect
                      x="10"
                      y="16"
                      width="4"
                      height="4"
                      rx="1"
                      fill="#fff"
                    />
                    <rect
                      x="18"
                      y="16"
                      width="4"
                      height="4"
                      rx="1"
                      fill="#fff"
                    />
                  </svg>
                  <span className="font-bold text-blue-700 text-base">
                    {batch.name}
                  </span>
                </div>
                <div className="text-gray-600 text-sm">
                  <span className="font-semibold">Days:</span> {batch.days}
                </div>
                <div className="text-gray-600 text-sm">
                  <span className="font-semibold">Time:</span> {batch.time}
                </div>
                <div className="text-gray-600 text-sm mb-2">
                  <span className="font-semibold">Mode:</span> {batch.mode}
                </div>
                <Link to="/signup" className="mt-1">
                  <button
                    className={`w-full rounded-md px-3 py-2 text-sm font-bold shadow ${
                      idx % 2 === 0
                        ? "bg-blue-600 hover:bg-blue-700 text-white"
                        : "bg-green-600 hover:bg-green-700 text-white"
                    } transition`}
                  >
                    Join Batch
                  </button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STUDENT TESTIMONIALS SECTION */}
      <section className="w-full py-8 bg-blue-50">
        <div className="max-w-5xl mx-auto px-4">
          <h3 className="text-lg sm:text-xl font-bold text-blue-900 mb-4 text-center">
            Student Testimonials
          </h3>
          <div className="flex flex-col md:flex-row gap-6 justify-center items-stretch">
            {testimonials.map((t, idx) => (
              <div
                key={idx}
                className="flex-1 bg-white rounded-xl shadow-md p-5 flex flex-col items-center text-center border border-blue-100"
              >
                <img
                  src={t.img}
                  alt={t.name}
                  className="w-16 h-16 rounded-full mb-2 border-2 border-blue-200 object-cover"
                />
                <p className="text-gray-700 text-sm mb-2 italic">“{t.quote}”</p>
                <span className="font-semibold text-blue-700 text-xs">
                  {t.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEARNING RESOURCES SECTION */}
      <section className="w-full py-8">
        <div className="max-w-5xl mx-auto px-4">
          <h3 className="text-lg sm:text-xl font-bold text-blue-900 mb-4 text-center">
            Learning Resources
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {resources.map((res, idx) => (
              <div
                key={idx}
                className="bg-white rounded-lg shadow-md p-4 border-l-4 border-green-500 hover:shadow-xl transition-all duration-500"
              >
                {/* Book SVG */}
                <div className="mb-2">
                  <svg
                    width="30"
                    height="30"
                    viewBox="0 0 30 30"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="3"
                      y="6"
                      width="24"
                      height="18"
                      rx="3"
                      fill="#bbf7d0"
                    />
                    <rect
                      x="6"
                      y="9"
                      width="18"
                      height="12"
                      rx="2"
                      fill="#22c55e"
                    />
                    <rect
                      x="8"
                      y="11"
                      width="14"
                      height="8"
                      rx="1"
                      fill="#fff"
                    />
                  </svg>
                </div>
                <div className="text-base font-bold text-green-700 mb-1 transition-all duration-500">
                  {res.title}
                </div>
                <div className="text-gray-600 text-sm transition-all duration-500">
                  {res.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default Learn;
